'use client'

import { documentToReactComponents } from '@contentful/rich-text-react-renderer'
import ClientLink from '@susu/headless-commerce/components/Link/ClientLink'
import { useCountry } from '@susu/headless-commerce/contexts/country/client'
import { useCurrencies } from '@susu/headless-commerce/contexts/currencies/client'
import type {
  Button,
  NavigationLink,
} from '@susu/headless-commerce/gql/generated/graphql'
import { usePageType } from '@susu/headless-commerce/hooks/usePageType'
import { generateUrlFromLinkContent } from '@susu/headless-commerce/utils/UrlGenerator'
import { createRenderNodeForPriceInfo } from '@susu/headless-commerce/utils/contentfulUtils'
import {
  trackClickPromotion,
  trackImpressionPromotion,
} from '@susu/headless-commerce/utils/tracking/tracking'
import classNames from 'classnames'
import type { Maybe } from 'graphql/jsutils/Maybe'
import { useCallback, useEffect, useRef } from 'react'

import { useLocale } from '@susu/headless-commerce/contexts/locale/client'
import styles from './ContentPageHeroBannerSection.module.scss'

export type ClientHeroButtonProps = {
  showButtonOnDesktop: Maybe<boolean> | null
  showButtonOnMobile: Maybe<boolean> | null
  showButtonOnTablet: Maybe<boolean> | null
  button: Button
}

export const ClientHeroButton = ({
  button,
  showButtonOnDesktop,
  showButtonOnMobile,
  showButtonOnTablet,
}: ClientHeroButtonProps) => {
  const heroButtonRef = useRef<HTMLAnchorElement>(null)
  const locale = useLocale()
  const country = useCountry()
  const currencies = useCurrencies()
  const pageType = usePageType()

  const { link, buttonRichText } = button ?? {}
  const { promotionEvents } = link ?? {}

  const heroButtonClasses = classNames(
    'body-small-light',
    styles['content-hero__button'],
    {
      [styles['content-hero__button--mobile-hidden']]: !showButtonOnMobile,
      [styles['content-hero__button--tablet-hidden']]: !showButtonOnTablet,
      [styles['content-hero__button--desktop-hidden']]: !showButtonOnDesktop,
      [styles['content-hero__button--secondary-outline']]:
        button?.buttonStyle === 'secondary-white-outline',
    },
  )

  const buttonLabel = documentToReactComponents(
    buttonRichText?.json,
    createRenderNodeForPriceInfo(
      buttonRichText,
      country.ecommerce.currencyCode,
      country,
      currencies,
    ),
  )

  const handleHeroButtonClick = useCallback(() => {
    if (!promotionEvents) {
      return
    }

    trackClickPromotion(heroButtonRef, promotionEvents, pageType, {
      locale,
      country,
    })
  }, [country, locale, promotionEvents, pageType])

  useEffect(() => {
    if (!promotionEvents) {
      return
    }

    trackImpressionPromotion(heroButtonRef, promotionEvents, pageType, {
      locale,
      country,
    })
  }, [country, locale, promotionEvents, pageType])

  return (
    <ClientLink
      className={heroButtonClasses}
      onClick={handleHeroButtonClick}
      ref={heroButtonRef}
      href={generateUrlFromLinkContent(
        link as NavigationLink,
        country.siteID,
        locale,
      )}
      automationId={String(link?.automationId)}
    >
      {buttonLabel}
    </ClientLink>
  )
}
